'use client';

import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useRegionalFormatters } from '@/lib/use-regional-formatters';

interface SectionProps<T> {
  config: T;
  locale: string;
}

export function HeroSection({ config, locale }: SectionProps<any>) {
  if (!config.headline) throw new Error('Hero section missing required field: headline');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated gradient background with parallax */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-green-600 animate-gradient-xy" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
      
      {/* Mesh gradient overlay with mouse tracking */}
      <div className="absolute inset-0 opacity-60" style={{
        background: `radial-gradient(circle 800px at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.15), transparent 40%)`
      }} />
      
      {/* Animated grid with parallax */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.03)_2px,transparent_2px),linear-gradient(90deg,rgba(255,255,255,.03)_2px,transparent_2px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" style={{ transform: `translateY(${scrollY * 0.3}px)` }} />
      
      {/* Floating orbs with different animations */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-green-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
      
      {/* Spotlight effect */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
      
      <div className="relative z-10 container mx-auto px-6 py-32 text-center">
        {/* Badge */}
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-full text-white text-sm font-bold mb-8 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer group">
            <div className="flex -space-x-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white" />
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white" />
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-2 border-white" />
            </div>
            <span>Trusted by 10,000+ organizations worldwide</span>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          </div>
        </div>
        
        {/* Headline with character-by-character animation */}
        <div className={`transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-[10rem] font-black mb-10 text-white leading-[0.9] tracking-tighter">
            {config.headline.split(' ').map((word: string, i: number) => (
              <span key={i} className="inline-block mr-4 md:mr-8 hover:scale-110 hover:text-yellow-300 transition-all duration-300 cursor-default" style={{ animationDelay: `${i * 100}ms` }}>
                {word}
              </span>
            ))}
          </h1>
        </div>
        
        {config.subheadline && (
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-2xl md:text-3xl lg:text-4xl text-white/95 mb-16 max-w-5xl mx-auto font-light leading-relaxed backdrop-blur-sm">
              {config.subheadline}
            </p>
          </div>
        )}
        
        {config.primaryCTA && (
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center mb-16`}>
            <Button size="lg" className="group relative h-16 sm:h-20 px-8 sm:px-16 text-lg sm:text-2xl font-black bg-white text-blue-600 hover:bg-white overflow-hidden shadow-2xl hover:shadow-white/50" asChild>
              <Link href={config.primaryCTA.href}>
                <span className="relative z-10 flex items-center gap-3">
                  {config.primaryCTA.text}
                  <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-white absolute inset-0 flex items-center justify-center gap-3">
                  {config.primaryCTA.text}
                  <span>→</span>
                </span>
              </Link>
            </Button>
            {config.secondaryCTA && (
              <Button size="lg" variant="outline" className="group h-16 sm:h-20 px-8 sm:px-16 text-lg sm:text-2xl font-black border-3 border-white/40 text-white hover:bg-white/20 backdrop-blur-2xl hover:border-white hover:scale-105 transition-all duration-300 shadow-2xl" asChild>
                <Link href={config.secondaryCTA.href}>
                  <span className="flex items-center gap-3">
                    <span className="text-3xl">▶</span>
                    {config.secondaryCTA.text}
                  </span>
                </Link>
              </Button>
            )}
          </div>
        )}
        
        {config.trustBadges && config.trustBadges.length > 0 && (
          <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} flex gap-6 justify-center flex-wrap`}>
            {config.trustBadges.map((badge: string, i: number) => (
              <div key={i} className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 via-purple-400 to-green-400 rounded-2xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500" />
                <div className="relative px-8 py-4 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl text-white font-bold hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-110 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {badge}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Animated scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer group" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
        <div className="w-8 h-14 border-3 border-white/40 rounded-full flex justify-center p-2 group-hover:border-white transition-colors">
          <div className="w-2 h-4 bg-white/70 rounded-full animate-pulse" />
        </div>
        <p className="text-white/60 text-sm mt-3 font-semibold group-hover:text-white transition-colors">Scroll to explore</p>
      </div>
    </section>
  );
}

export function FeaturesSection({ config, locale }: SectionProps<any>) {
  if (!config.features) throw new Error('Features section missing required field: features');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  return (
    <section className="relative py-40 px-4 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-blue-50/50 to-purple-50/50 dark:from-gray-950 dark:via-blue-950/30 dark:to-purple-950/30" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.1),transparent_50%),radial-gradient(circle_at_70%_50%,rgba(168,85,247,0.1),transparent_50%)]" />
      
      <div className="relative container mx-auto max-w-7xl">
        {config.title && (
          <div className="text-center mb-8">
            <div className="inline-block px-6 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 font-semibold mb-6">
              ✨ Features
            </div>
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-black mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent leading-tight">
              {config.title}
            </h2>
          </div>
        )}
        {config.subtitle && (
          <p className="text-2xl md:text-3xl text-center text-gray-600 dark:text-gray-400 mb-24 max-w-4xl mx-auto font-light">{config.subtitle}</p>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {config.features.map((feature: any, i: number) => {
            const content = (
              <>
                <div className={`absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-all duration-700`} />
                <div className="relative p-10 bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 group-hover:border-transparent transition-all duration-500 h-full">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ padding: '2px', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }} />
                  <div className="relative">
                    <div className="relative w-16 h-16 mb-8">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-green-500 rounded-2xl opacity-20 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6" />
                      <div className="absolute inset-0 flex items-center justify-center text-3xl font-black bg-gradient-to-br from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {i + 1}
                      </div>
                    </div>
                    <h3 className="text-3xl font-black mb-5 text-gray-900 dark:text-white group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-500">
                      {feature.title}
                    </h3>
                    <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-500">
                      {feature.description}
                    </p>
                    {feature.link && (
                      <div className="mt-6 flex items-center text-blue-600 dark:text-blue-400 font-semibold opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-500">
                        Learn more →
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
            
            return feature.link ? (
              <Link
                key={i}
                href={feature.link.startsWith('/') ? feature.link : `/${feature.link}`}
                className="group relative block"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {content}
              </Link>
            ) : (
              <div
                key={i}
                className="group relative"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PricingSection({ config, locale, currency }: SectionProps<any> & { currency: string }) {
  if (!config.plans) throw new Error('Pricing section missing required field: plans');
  const [isAnnual, setIsAnnual] = useState(false);
  
  return (
    <section className="relative py-40 px-4 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_70%)]" />
      
      <div className="relative container mx-auto max-w-7xl">
        {config.title && (
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-green-100 dark:bg-green-900/30 rounded-full text-green-600 dark:text-green-400 font-semibold mb-6">
              💰 Pricing
            </div>
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-black mb-8 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent leading-tight">
              {config.title}
            </h2>
            
            {/* Billing toggle */}
            <div className="inline-flex items-center gap-4 p-2 bg-gray-100 dark:bg-gray-800 rounded-full">
              <button 
                onClick={() => setIsAnnual(false)}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${!isAnnual ? 'bg-white dark:bg-gray-700 shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setIsAnnual(true)}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${isAnnual ? 'bg-white dark:bg-gray-700 shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}
              >
                Annual
                <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">Save 20%</span>
              </button>
            </div>
          </div>
        )}
        
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {config.plans.map((plan: any, i: number) => (
            <div 
              key={i} 
              className={`group relative transition-all duration-700 ${
                plan.featured ? 'md:-mt-8 md:mb-8' : ''
              }`}
            >
              {/* Animated glow for featured */}
              {plan.featured && (
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 rounded-4xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity duration-700 animate-pulse" />
              )}
              
              <div className={`relative p-12 bg-white dark:bg-gray-900 rounded-4xl transition-all duration-500 h-full flex flex-col ${
                plan.featured 
                  ? 'border-2 border-transparent bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50 shadow-2xl' 
                  : 'border border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-2xl hover:-translate-y-2'
              }`}>
                {plan.featured && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                    <div className="px-8 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 text-white text-sm font-black rounded-full shadow-2xl animate-pulse">
                      ⭐ MOST POPULAR
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-10">
                  <h3 className="text-4xl font-black mb-6 text-gray-900 dark:text-white">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    {plan.price ? (
                      <>
                        <span className="text-7xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
                          ${isAnnual ? Math.floor(plan.price * 0.8) : plan.price}
                        </span>
                        <span className="text-2xl text-gray-600 dark:text-gray-400">/mo</span>
                      </>
                    ) : (
                      <span className="text-7xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">Free</span>
                    )}
                  </div>
                  {isAnnual && plan.price && (
                    <p className="text-sm text-gray-500 dark:text-gray-500">Billed ${plan.price * 12 * 0.8}/year</p>
                  )}
                </div>
                
                <Button className={`w-full h-14 text-lg font-bold mb-10 transition-all duration-500 group-hover:scale-105 ${
                  plan.featured
                    ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700 text-white shadow-2xl shadow-blue-500/50'
                    : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-blue-600 dark:hover:bg-blue-600 hover:text-white'
                }`}>
                  Get Started →
                </Button>
                
                <ul className="space-y-5 flex-grow">
                  {plan.features?.map((feature: string, j: number) => (
                    <li key={j} className="flex items-start gap-4 group/item">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center group-hover/item:scale-110 transition-transform duration-300">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-lg text-gray-700 dark:text-gray-300 group-hover/item:text-gray-900 dark:group-hover/item:text-white transition-colors duration-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection({ config, locale }: SectionProps<any>) {
  if (!config.testimonials) throw new Error('Testimonials section missing required field: testimonials');
  
  return (
    <section className="py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        {config.title && (
          <h2 className="text-4xl font-bold text-center mb-12">{config.title}</h2>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {config.testimonials.map((testimonial: any, i: number) => (
            <div key={i} className="p-6 bg-white/50 dark:bg-gray-800/50 rounded-2xl">
              <p className="text-gray-700 dark:text-gray-300 mb-4">"{testimonial.content}"</p>
              <div className="font-semibold">{testimonial.author}</div>
              {testimonial.role && (
                <div className="text-sm text-gray-600 dark:text-gray-400">{testimonial.role}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StatsSection({ config }: { config: any }) {
  if (!config.stats) throw new Error('Stats section missing required field: stats');
  
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 dark:from-blue-900 dark:via-blue-950 dark:to-green-900" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnpNNiAzNGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTM2IDM0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
      <div className="relative z-10 container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-3 gap-12 text-center">
          {config.stats.map((stat: any, i: number) => (
            <div key={i} className="group">
              <div className="text-6xl md:text-7xl font-black mb-4 text-white drop-shadow-2xl group-hover:scale-110 transition-transform duration-300">{stat.value}</div>
              <div className="text-xl md:text-2xl text-blue-100 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTASection({ config, locale }: SectionProps<any>) {
  if (!config.headline) throw new Error('CTA section missing required field: headline');
  
  return (
    <section className="relative py-32 px-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 dark:from-blue-900 dark:via-blue-950 dark:to-green-900" />
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnpNNiAzNGMzLjMxIDAgNiAyLjY5IDYgNnMtMi42OSA2LTYgNi02LTIuNjktNi02IDIuNjktNiA2LTZ6TTM2IDM0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
      <div className="absolute inset-0 backdrop-blur-3xl" />
      
      <div className="relative z-10 container mx-auto max-w-5xl text-center">
        <h2 className="text-5xl md:text-6xl font-black mb-6 text-white leading-tight">{config.headline}</h2>
        {config.description && (
          <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto font-light">{config.description}</p>
        )}
        {config.primaryCTA && (
          <div className="flex gap-6 justify-center flex-wrap">
            <Button size="lg" className="h-16 px-10 text-lg font-bold bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105" asChild>
              <Link href={config.primaryCTA.href}>{config.primaryCTA.text}</Link>
            </Button>
            {config.secondaryCTA && (
              <Button size="lg" variant="outline" className="h-16 px-10 text-lg font-bold border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm transition-all duration-300 hover:scale-105" asChild>
                <Link href={config.secondaryCTA.href}>{config.secondaryCTA.text}</Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export function LogoCloudSection({ config }: { config: any }) {
  if (!config.logos) throw new Error('Logo cloud section missing required field: logos');
  
  return (
    <section className="py-20 px-4 bg-white dark:bg-gray-950">
      <div className="container mx-auto max-w-6xl">
        {config.title && (
          <p className="text-center text-gray-500 dark:text-gray-500 mb-12 text-lg font-medium uppercase tracking-wider">{config.title}</p>
        )}
        <div className="flex flex-wrap justify-center items-center gap-12">
          {config.logos.map((logo: any, i: number) => (
            <div key={i} className="text-2xl font-bold text-gray-400 dark:text-gray-600 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 cursor-pointer">{logo.name}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQSection({ config }: { config: any }) {
  if (!config.items) throw new Error('FAQ section missing required field: items');
  
  return (
    <section className="py-32 px-4 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto max-w-4xl">
        {config.title && (
          <h2 className="text-5xl md:text-6xl font-black text-center mb-20 bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">{config.title}</h2>
        )}
        <div className="space-y-6">
          {config.items.map((item: any, i: number) => (
            <div key={i} className="group p-8 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-xl transition-all duration-300">
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{item.question}</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContentSection({ config }: { config: any }) {
  if (!config.content) throw new Error('Content section missing required field: content');
  
  return (
    <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto max-w-4xl">
        <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-black prose-headings:tracking-tight prose-h2:text-4xl prose-h2:mb-6 prose-h2:bg-gradient-to-r prose-h2:from-blue-600 prose-h2:to-green-600 prose-h2:bg-clip-text prose-h2:text-transparent prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:space-y-2 prose-li:text-gray-700 dark:prose-li:text-gray-300" dangerouslySetInnerHTML={{ __html: config.content }} />
      </div>
    </section>
  );
}

export function FormSection({ config, locale }: SectionProps<any>) {
  if (!config.formType) throw new Error('Form section missing required field: formType');
  
  return (
    <section className="py-32 px-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      <div className="container mx-auto max-w-2xl">
        {config.title && (
          <h2 className="text-5xl md:text-6xl font-black text-center mb-6 bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">{config.title}</h2>
        )}
        {config.description && (
          <p className="text-center text-xl text-gray-600 dark:text-gray-400 mb-12">{config.description}</p>
        )}
        <div className="p-12 bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 shadow-2xl">
          <p className="text-center text-gray-500 dark:text-gray-400 text-lg">Contact form will be integrated here</p>
        </div>
      </div>
    </section>
  );
}
